export interface JobInfo {
  title: string;
  company: string;
  location?: string;
  date: string;
  id?: string;
}

export const extractJobInfoFromDescription = (jobDescription: string, t?: (key: string) => string): JobInfo => {
  const lines = jobDescription.split('\n').filter(line => line.trim().length > 0);
  
  let title = '';
  let company = '';
  let location = '';
  
  // Default translation function if not provided
  const translate = t || ((key: string) => key);
  
  // Patterns to detect job title
  const titlePatterns = [
    /(?:poste|offre|recherche|recrute|cherche)[\s:]*(.+?)(?:\n|$)/i,
    /^(.+?)(?:\s*-\s*|\s*\|\s*|\s*chez\s*)/i,
    /^([A-Z][^.!?]*?)(?:\s*$|\s*\n)/m
  ];
  
  // Patterns to detect company
  const companyPatterns = [
    /(?:chez|entreprise|société|company|pour)[\s:]*([A-Z][^.\n]*?)(?:\n|$)/i,
    /([A-Z][A-Za-z\s&]+(?:SA|SAS|SARL|Ltd|Inc|GmbH|AG))/,
    /^([A-Z][A-Za-z\s&]{2,30})(?:\s*-|\s*\||\s*recrute|\s*recherche)/im
  ];
  
  // Patterns to detect location
  const locationPatterns = [
    /(?:lieu|localisation|location|ville|city)[\s:]*([^.\n]+)/i,
    /(\d{5}\s+[A-Z][a-z]+)/,
    /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s*(?:France|Suisse|Belgique|Canada)/i,
    /(Paris|Lyon|Marseille|Toulouse|Nice|Nantes|Strasbourg|Montpellier|Bordeaux|Lille|Rennes|Reims|Le Havre|Saint-Étienne|Toulon|Grenoble|Dijon|Angers|Nîmes|Villeurbanne|Clermont-Ferrand|Le Mans|Aix-en-Provence|Brest|Tours|Amiens|Limoges|Annecy|Perpignan|Boulogne-Billancourt|Metz|Besançon|Orléans|Saint-Denis|Argenteuil|Rouen|Mulhouse|Montreuil|Caen|Nancy)/i
  ];
  
  // Extract job title
  for (const pattern of titlePatterns) {
    const match = jobDescription.match(pattern);
    if (match && match[1]) {
      title = match[1].trim();
      // Clean title
      title = title.replace(/[^\w\s\-\/]/g, '').trim();
      if (title.length > 5 && title.length < 100) {
        break;
      }
    }
  }
  
  // If no title found, take the first significant line
  if (!title) {
    for (const line of lines.slice(0, 3)) {
      const cleanLine = line.trim();
      if (cleanLine.length > 10 && cleanLine.length < 80 && 
          !cleanLine.includes('@') && !cleanLine.includes('http') &&
          /^[A-Z]/.test(cleanLine)) {
        title = cleanLine;
        break;
      }
    }
  }
  
  // Extract company
  for (const pattern of companyPatterns) {
    const match = jobDescription.match(pattern);
    if (match && match[1]) {
      company = match[1].trim();
      // Clean company name
      company = company.replace(/[^\w\s\-&]/g, '').trim();
      if (company.length > 2 && company.length < 50) {
        break;
      }
    }
  }
  
  // Extract location
  for (const pattern of locationPatterns) {
    const match = jobDescription.match(pattern);
    if (match && match[1]) {
      location = match[1].trim();
      if (location.length > 2 && location.length < 50) {
        break;
      }
    }
  }
  
  // Generate unique ID based on title and company
  const id = generateJobId(title, company);
  
  return {
    title: title || translate('job_extractor.position_not_detected'),
    company: company || translate('job_extractor.company_not_detected'),
    location: location || undefined,
    date: new Date().toLocaleDateString('fr-FR'),
    id
  };
};

export const generateJobId = (title: string, company: string): string => {
  const normalized = `${title}-${company}`.toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, '-');
  
  // Add simple hash to avoid collisions
  const hash = normalized.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  return `${normalized}-${Math.abs(hash)}`;
};

export const checkDuplicateApplication = (jobId: string): boolean => {
  try {
    const applications = JSON.parse(localStorage.getItem('cv_analyzer_applications') || '[]');
    return applications.includes(jobId);
  } catch {
    return false;
  }
};

export const saveJobApplication = (jobInfo: JobInfo): void => {
  try {
    const applications = JSON.parse(localStorage.getItem('cv_analyzer_applications') || '[]');
    if (jobInfo.id && !applications.includes(jobInfo.id)) {
      applications.push(jobInfo.id);
      // localStorage.setItem('cv_analyzer_applications', JSON.stringify(applications));
      
      // Also save job details
      const jobDetails = JSON.parse(localStorage.getItem('cv_analyzer_job_details') || '{}');
      jobDetails[jobInfo.id] = jobInfo;
      // localStorage.setItem('cv_analyzer_job_details', JSON.stringify(jobDetails));
    }
  } catch (error) {
    console.error('Error saving application:', error);
  }
};

export const getJobApplications = (): JobInfo[] => {
  try {
    const jobDetails = JSON.parse(localStorage.getItem('cv_analyzer_job_details') || '{}');
    return Object.values(jobDetails);
  } catch {
    return [];
  }
};